import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../App'
import { api, Conversation, Group } from '../services/api'
import { useSharedWebSocket } from '../contexts/WebSocketContext'

type TabType = 'chats' | 'groups' | 'profile'

export default function ChatListPage() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabType>('chats')
  const [showNewChatModal, setShowNewChatModal] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [newChatUsername, setNewChatUsername] = useState('')
  const [swipingId, setSwipingId] = useState<string | null>(null)
  const [swipeX, setSwipeX] = useState(0)
  const touchStartRef = useRef<number>(0)
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { isConnected, lastMessage } = useSharedWebSocket()

  useEffect(() => { loadConversations(); loadGroups() }, [])

  const loadGroups = async () => { try { setGroups(await api.getGroups()) } catch {} }

  useEffect(() => { if (lastMessage?.type === 'message' || lastMessage?.type === 'new_message') loadConversations() }, [lastMessage])

  useEffect(() => {
    if (isConnected) return
    const id = setInterval(loadConversations, 5000)
    return () => clearInterval(id)
  }, [isConnected])

  const loadConversations = async () => {
    try { setIsLoading(true); setConversations(await api.getConversations()) }
    catch {} finally { setIsLoading(false) }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp); const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    if (days > 7) return date.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' })
    if (days > 0) return `${days}天前`
    const hours = Math.floor(diff / (1000 * 60 * 60)); if (hours > 0) return `${hours}小時前`
    const minutes = Math.floor(diff / (1000 * 60)); if (minutes > 0) return `${minutes}分鐘前`
    return '剛剛'
  }

  const getLastMessagePreview = (conv: Conversation): string => {
    if (!conv.lastMessage) return '開始對話'
    const text = conv.lastMessage.text || conv.lastMessage.ciphertext
    if (!text) return '🔒 加密訊息'
    if (text.startsWith('[MEDIA]')) {
      try {
        const media = JSON.parse(text.slice(7))
        if (media.type === 'image') return '📷 圖片'
        if (media.type === 'video') return '🎬 影片'
        if (media.type === 'audio') return '🎤 語音訊息'
        return '📎 檔案'
      } catch { return '📎 媒體' }
    }
    if (text.startsWith('PLAIN:') || text.length > 100) return '🔒 加密訊息'
    return text
  }

  const filteredConversations = conversations.filter(conv =>
    (conv.recipientName || conv.recipientId).toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleStartNewChat = async () => {
    if (!newChatUsername.trim()) return
    try {
      const users = await api.searchUsers(newChatUsername.trim())
      const exact = users.find(u => u.username.toLowerCase() === newChatUsername.trim().toLowerCase())
      navigate(`/chat/${exact ? exact.id : newChatUsername.trim()}`)
    } catch { navigate(`/chat/${newChatUsername.trim()}`) }
    setShowNewChatModal(false); setNewChatUsername('')
  }

  const handleDeleteConversation = async (recipientId: string) => {
    if (!confirm('確定要刪除此對話嗎？')) return
    try { await api.deleteConversation(recipientId); setConversations(prev => prev.filter(c => c.recipientId !== recipientId)) } catch {}
    setSwipingId(null); setSwipeX(0)
  }

  // Swipe handlers
  const handleTouchStart = (id: string, e: React.TouchEvent) => { touchStartRef.current = e.touches[0].clientX; setSwipingId(id) }
  const handleTouchMove = (e: React.TouchEvent) => { if (!swipingId) return; const dx = touchStartRef.current - e.touches[0].clientX; setSwipeX(Math.max(0, Math.min(dx, 80))) }
  const handleTouchEnd = () => { if (swipeX < 40) { setSwipeX(0); setSwipingId(null) } else { setSwipeX(80) } }

  return (
    <div className="min-h-screen flex flex-col">
      {/* App Bar */}
      <header className="app-bar">
        <h1 className="text-xl font-semibold flex-1">LYNKSTAR</h1>
        <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-secure-green' : 'bg-gray-400'}`} />
        <button onClick={() => setShowSearch(!showSearch)} className="p-2 hover:bg-white/10 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg></button>
        <div className="relative">
          <button onClick={() => setShowMenu(!showMenu)} className="p-2 hover:bg-white/10 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></svg></button>
          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface rounded-xl shadow-lg z-50 overflow-hidden animate-fade-in">
              <button onClick={() => { setShowMenu(false); navigate('/group/create') }} className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>建立群組</button>
              <button onClick={() => { setShowMenu(false); navigate('/settings') }} className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>設定</button>
              <button onClick={() => { logout(); navigate('/login') }} className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>登出</button>
            </div>
          )}
        </div>
      </header>

      {/* Search Bar */}
      {showSearch && (
        <div className="px-4 py-2 bg-white dark:bg-dark-surface border-b border-gray-100 dark:border-gray-800">
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="搜尋對話..." className="input-field" autoFocus />
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64"><div className="spinner border-primary border-t-primary-light" /></div>
        ) : activeTab === 'chats' ? (
          filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-8">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-gray-300 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
              <h2 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">還沒有對話</h2>
              <p className="text-gray-400 dark:text-gray-500">開始新的聊天吧</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {filteredConversations.map((conv) => (
                <div key={conv.recipientId} className="relative overflow-hidden"
                  onTouchStart={(e) => handleTouchStart(conv.recipientId, e)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {/* Delete button (revealed on swipe) */}
                  <div className="absolute right-0 top-0 bottom-0 w-20 bg-red-500 flex items-center justify-center">
                    <button onClick={() => handleDeleteConversation(conv.recipientId)} className="text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                  <button
                    onClick={() => navigate(`/chat/${conv.recipientId}`)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-dark-surface transition-all bg-white dark:bg-dark-bg relative"
                    style={{ transform: swipingId === conv.recipientId ? `translateX(-${swipeX}px)` : undefined }}
                  >
                    <div className="relative">
                      <div className="avatar">
                        {conv.recipientAvatar ? (
                          <img src={conv.recipientAvatar} alt={conv.recipientName || conv.recipientId} className="w-full h-full object-cover rounded-full" />
                        ) : (conv.recipientName || conv.recipientId).charAt(0).toUpperCase()}
                      </div>
                      {conv.isOnline && <div className="absolute bottom-0 right-0 online-indicator" />}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">{conv.recipientName || conv.recipientId}</h3>
                        {conv.lastMessage && <span className={`text-xs ${conv.unreadCount > 0 ? 'text-primary font-medium' : 'text-gray-400'}`}>{formatTime(conv.lastMessage.timestamp)}</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-secure-green flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate flex-1">{getLastMessagePreview(conv)}</p>
                        {conv.unreadCount > 0 && <span className="unread-badge">{conv.unreadCount > 99 ? '99+' : conv.unreadCount}</span>}
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )
        ) : (
          /* Groups Tab */
          groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-8">
              <h2 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">還沒有群組</h2>
              <button onClick={() => navigate('/group/create')} className="btn-primary mt-4">建立群組</button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {groups.map((group) => (
                <button key={group.id} onClick={() => navigate(`/group/${group.id}`)} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-dark-surface">
                  <div className="avatar bg-primary">{group.avatar_url ? <img src={group.avatar_url} alt={group.name} className="w-full h-full object-cover rounded-full" /> : <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}</div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between mb-1"><h3 className="font-semibold text-gray-900 dark:text-white truncate">{group.name}</h3><span className="text-xs text-gray-400">{group.member_count} 位成員</span></div>
                    {(group.my_role === 'owner' || group.my_role === 'admin') && <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">{group.my_role === 'owner' ? '擁有者' : '管理員'}</span>}
                  </div>
                </button>
              ))}
            </div>
          )
        )}
      </main>

      {/* FAB */}
      <button onClick={() => activeTab === 'chats' ? setShowNewChatModal(true) : navigate('/group/create')} className="fab fixed right-6 bottom-24">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={activeTab === 'chats' ? "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" : "M12 6v6m0 0v6m0-6h6m-6 0H6"} /></svg>
      </button>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <button onClick={() => setActiveTab('chats')} className={`bottom-nav-item ${activeTab === 'chats' ? 'active' : ''}`}><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'chats' ? 2.5 : 2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg><span className="text-xs">聊天</span></button>
        <button onClick={() => setActiveTab('groups')} className={`bottom-nav-item ${activeTab === 'groups' ? 'active' : ''}`}><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'groups' ? 2.5 : 2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg><span className="text-xs">群組</span></button>
        <button onClick={() => navigate('/profile')} className="bottom-nav-item"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg><span className="text-xs">個人</span></button>
      </nav>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setShowNewChatModal(false)}>
          <div className="bg-white dark:bg-dark-surface w-full max-w-lg rounded-t-2xl p-6 animate-slide-up" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">開始新對話</h2>
            <div className="relative mb-4"><div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg></div>
              <input type="text" value={newChatUsername} onChange={e => setNewChatUsername(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleStartNewChat()} placeholder="輸入用戶名" className="input-field pl-12" autoFocus />
            </div>
            <button onClick={handleStartNewChat} disabled={!newChatUsername.trim()} className="btn-primary w-full mb-4">開始聊天</button>
          </div>
        </div>
      )}

      {showMenu && <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />}
    </div>
  )
}
